import { Grid } from '@mui/material'
import { Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { default as React, useEffect, useState } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../../common/roleHelper'
import { getErrMsg, toastNeg, toastPos } from '../../../../common/toastHelper'
import {
  IDropdownProjects,
  IEditUserProject,
  ILinkedSkill,
  IProjectFormPackage,
  IUserProject,
} from '../../../../interfaces/projects/IProjects'
import { ISkillProject } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import DeleteConfirmation from '../../../OPcomponents/DeleteConfirmation'
import { ExitConfirmation } from '../../../OPcomponents/ExitConfirmation'
import AutoSave from '../../../OPcomponents/FormikAutosave'
import { ITableForm } from '../../../OPcomponents/OPNewTable'
import { newPersonalProjectSchema } from '../../../OPcomponents/ValidationSchemas'
import { OPSkillsButtons } from '../../OPSkillsButtons'
import UserSkillForm from './UserSkillForm'

const UserSkillEdit = (props: ITableForm): JSX.Element => {
  const { collapseHandler } = props
  const data = props.data as IProjectFormPackage
  const rootStore = useStore()
  const { t } = useTranslation()
  const { validatedUser } = rootStore.loginStore
  const { setCurrentProject, getAllRoles, deleteUserProject, editUserProject, getAllProjects } = rootStore.projectStore

  const { getUserPublicWorkHistory, getUserPrivateWorkHistory, getUserEmployersJobTitlesWorkHistory } = rootStore.personStore

  const [linkedSkills, setLinkedSkills] = useState<ISkillProject[]>([])

  const [deleteProjectConfirmation, setDeleteProjectConfirmation] = useState(false)

  useEffect(() => {
    getAllRoles()
  }, [getAllRoles, validatedUser])

  // Initialize form values with proper unit conversion
  const rawManDays = data?.userDetails.details.manDays ?? 0
  const rawManMonths = data?.userDetails.details.manMonths ?? 0
  const rawUsingManDays = data?.userDetails.details.usingManDays ? true : false

  let initManDays = rawManDays
  let initManMonths = rawManMonths

  // Ensure values are consistent with the selected unit
  if (!rawUsingManDays && rawManMonths === 0 && rawManDays > 0) {
    // Using manmonths mode but manmonths is 0 while mandays has value - convert
    initManMonths = Math.round(rawManDays / 21)
    initManDays = 0
  } else if (rawUsingManDays && rawManDays === 0 && rawManMonths > 0) {
    // Using mandays mode but mandays is 0 while manmonths has value - convert
    initManDays = Math.round(rawManMonths * 21)
    initManMonths = 0
  }

  const editOldInit: IUserProject = {
    userId: validatedUser,
    projectId: data?.userDetails.details.projectId,
    startDate: data?.userDetails.details.startDate,
    endDate: data?.userDetails.details.endDate,
    manDays: initManDays,
    manMonths: initManMonths,
    usingManDays: rawUsingManDays,
    usingManMonths: false,
    descriptionFi: data?.userDetails.details.descriptionFi ?? '',
    descriptionEng: data?.userDetails.details.descriptionEng ?? '',
    roleIds:
      data?.userDetails.details.roles.map((role) => ({
        value: role.roleId.toString(),
        key: role.roleId,
        text: getLocalizedRoleName(role),
      })) ?? [],
    isKeyProject: data?.userDetails.details.isKeyProject ?? false,
  }

  useEffect(() => {
    const links: ISkillProject[] = []

    if (data?.userDetails.details.linkedSkills) {
      data?.userDetails.details.linkedSkills.forEach((skill: ILinkedSkill) => {
        links.push({
          userId: validatedUser,
          projectId: data?.userDetails.details.projectId,
          skillId: skill.skillId,
          textFi: skill.skillNameFi,
          textEng: getI18n().language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi,
        })
      })
    }
    setLinkedSkills(links)
  }, [data?.userDetails.details.linkedSkills, data?.userDetails.details.projectId, validatedUser])

  const handleClose = async (isDelete = false) => {
    collapseHandler?.()
    setTimeout(async () => {
      await getUserPublicWorkHistory(validatedUser, false)
      await getUserPrivateWorkHistory(validatedUser, false)
      await getUserEmployersJobTitlesWorkHistory(validatedUser, false)
      setCurrentProject({})
      if (isDelete) await getAllProjects(validatedUser, false)
    }, 500)
  }

  const deleteProject = async (id: number) => {
    try {
      await deleteUserProject(validatedUser, id, true)
      toastPos(t('Toasts.ProjectUserDelete'), true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectUserDeleteError'), true)
    } finally {
      handleClose(true)
    }
  }
  const submitEdit = async (values: IUserProject) => {
    const roles = values.roleIds.map((role: IDropdownProjects) => role.key)
    const workLoad = values.usingManDays ? values.manDays : values.manMonths

    const editedProject: IEditUserProject = {
      userId: validatedUser,
      projectId: values.projectId,
      startDate: values.startDate,
      endDate: values.endDate,
      workAmount: workLoad,
      usingManDays: values.usingManDays,
      usingManMonths: !values.usingManDays,
      descriptionFi: values.descriptionFi,
      descriptionEng: values.descriptionEng,
      roleIds: roles || [],
      isKeyProject: values.isKeyProject,
    }

    try {
      await editUserProject(editedProject, true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectUserEditError'), true)
    }
  }

  useEffect(() => {
    return () => {
      handleClose()
    }
  }, [])

  return (
    <Grid item xs={12}>
      <Formik
        initialValues={editOldInit}
        onSubmit={(values) => submitEdit(values)}
        validationSchema={newPersonalProjectSchema}
        enableReinitialize
      >
        {({ values, handleSubmit, isValid, dirty, isSubmitting }) => (
          <Grid container rowSpacing={4} columnSpacing={2} alignItems='center'>
            <ExitConfirmation when={dirty} />
            <Grid xs={12} item>
              <UserSkillForm
                isTable
                deleteProjectConfirmation={deleteProjectConfirmation}
                setProjectSkills={setLinkedSkills}
                projectSkills={linkedSkills}
              />
            </Grid>
            <Grid xs={12} item>
              <OPSkillsButtons
                isDelete
                onDelete={() => setDeleteProjectConfirmation(true)}
                onClose={handleClose}
                bothDisabled={isSubmitting || deleteProjectConfirmation}
                onSubmit={handleSubmit}
                disabled={!isValid || isSubmitting}
                onAutosave={data?.userHas}
              />
            </Grid>
            <Grid item />
            <DeleteConfirmation
              open={deleteProjectConfirmation}
              whatToDelete='Project'
              deleteHandler={() => deleteProject(values.projectId)}
              handleClose={() => setDeleteProjectConfirmation(false)}
            />
            <AutoSave />
          </Grid>
        )}
      </Formik>
    </Grid>
  )
}

export default observer(UserSkillEdit)
